import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import {
  CHUNK_REFRESH_KEY,
  getStorageItem,
  isChunkLoadError,
  setStorageItem,
} from '../../Utils/lazyWithRetry';

interface ChunkErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
}

class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: unknown): ChunkErrorBoundaryState {
    if (isChunkLoadError(error)) {
      return { hasError: true };
    }
    // Re-throw non-chunk errors so outer ErrorBoundary handles them
    throw error;
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      const pageAlreadyRefreshed = getStorageItem(CHUNK_REFRESH_KEY) === 'true';
      if (!pageAlreadyRefreshed) {
        setStorageItem(CHUNK_REFRESH_KEY, 'true');
        if (typeof window !== 'undefined' && window.location) {
          window.location.reload();
        }
      }
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            p: 4,
            textAlign: 'center',
          }}
        >
          <CircularProgress sx={{ mb: 2, color: '#FF8201' }} />
          <Typography variant="h6" sx={{ fontFamily: 'IBM Plex Sans Condensed, sans-serif' }}>
            Mise à jour disponible, rechargement en cours...
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
